import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import JobCard from "@/components/JobCard";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const todayISO = () => new Date().toISOString().slice(0, 10);

const PropertyDetail = () => {
    const { id } = useParams();
    const nav = useNavigate();
    const [prop, setProp] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ service_type_id: "", scheduled_date: todayISO(), notes: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [p, j, s] = await Promise.all([
                api.get(`/properties/${id}`),
                api.get("/jobs", { params: { property_id: id } }),
                api.get("/service-types"),
            ]);
            setProp(p.data);
            setJobs(j.data);
            setTypes(s.data);
            const c = await api.get(`/customers/${p.data.customer_id}`);
            setCustomer(c.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Load failed");
            nav("/customers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [id]);

    const openNew = () => {
        setForm({
            service_type_id: types[0]?.id || "",
            scheduled_date: todayISO(),
            notes: "",
        });
        setOpen(true);
    };

    const create = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/jobs", { property_id: id, ...form });
            setOpen(false);
            toast.success("Job created");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const complete = async (jobId) => {
        try {
            await api.post(`/jobs/${jobId}/complete`);
            toast.success("Marked complete — next service scheduled");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const del = async (jobId) => {
        if (!confirm("Delete this job?")) return;
        try {
            await api.delete(`/jobs/${jobId}`);
            toast.success("Job deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const typeName = (tid) => types.find((t) => t.id === tid)?.name || "Service";

    if (loading) return <p className="text-label">Loading…</p>;
    if (!prop) return null;

    return (
        <div className="space-y-6" data-testid="property-detail-page">
            {customer && (
                <Link
                    to={`/customers/${customer.id}`}
                    className="inline-flex items-center gap-2 text-sm font-mono"
                    data-testid="back-to-customer"
                >
                    <ArrowLeft size={14} /> {customer.name}
                </Link>
            )}

            <div className="dh-card p-6">
                <p className="text-label">Property</p>
                <h2 className="font-display font-black text-3xl uppercase tracking-tight leading-none">
                    {prop.address}
                </h2>
                {customer && (
                    <p className="text-sm text-neutral-600 mt-2 font-mono">
                        {customer.email || "—"} · {customer.mobile || "—"}
                    </p>
                )}
            </div>

            <div className="flex items-end justify-between">
                <div>
                    <p className="text-label">Work</p>
                    <h3 className="font-display font-black text-2xl uppercase tracking-tight leading-none">
                        Jobs
                    </h3>
                    <p className="text-neutral-600 text-sm mt-1">
                        Swipe right on mobile or tap “Mark complete” to schedule next year’s service.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button className="dh-btn" data-testid="new-job-btn" onClick={openNew}>
                            <Plus size={16} /> New job
                        </button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                        <DialogHeader>
                            <DialogTitle className="font-display uppercase">New job</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={create} className="space-y-4" data-testid="job-form">
                            <div>
                                <label className="text-label block mb-1">Service</label>
                                <Select
                                    value={form.service_type_id}
                                    onValueChange={(v) => setForm({ ...form, service_type_id: v })}
                                >
                                    <SelectTrigger
                                        className="dh-input rounded-none border-2 border-[color:var(--dh-line)]"
                                        data-testid="job-service-select"
                                    >
                                        <SelectValue placeholder="Pick a service" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                                        {types.map((t) => (
                                            <SelectItem key={t.id} value={t.id} data-testid={`service-opt-${t.id}`}>
                                                {t.name} · £{t.price}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-label block mb-1">Scheduled date</label>
                                <input
                                    type="date"
                                    required
                                    className="dh-input"
                                    data-testid="job-date-input"
                                    value={form.scheduled_date}
                                    onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-label block mb-1">Notes</label>
                                <textarea
                                    className="dh-input"
                                    rows={3}
                                    data-testid="job-notes-input"
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                />
                            </div>
                            <DialogFooter>
                                <button className="dh-btn" data-testid="job-save-btn" disabled={saving}>
                                    {saving ? "Saving…" : "Create job"}
                                </button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {jobs.length === 0 ? (
                <div className="dh-card p-6 text-center">
                    <p className="font-display uppercase font-bold">No jobs yet</p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="job-list">
                    {jobs.map((j) => (
                        <JobCard
                            key={j.id}
                            job={j}
                            serviceName={typeName(j.service_type_id)}
                            onComplete={complete}
                            onDelete={del}
                            onChange={load}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default PropertyDetail;
